import { Fragment, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  fetchAllRolePermissions,
  fetchModulesList,
  resetRolePermissions,
  PERMS,
} from "../../../services/permissionsApi";

/* ── Badge colour / label per permission ─────────────────────── */
const permColor = { view: "primary", add: "success", update: "warning", delete: "danger" };
const permLabel = { view: "View", add: "Create", update: "Update", delete: "Delete" };
const permTextClass = { view: "text-white", add: "text-white", update: "text-dark", delete: "text-white" };

const ManagePermissions = () => {
  const navigate  = useNavigate();
  const [entries,  setEntries]  = useState([]);
  const [modules,  setModules]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  /* ── Load ─────────────────────────────────────────────────── */
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([fetchAllRolePermissions(), fetchModulesList()])
      .then(([data, mods]) => {
        setEntries(data);
        setModules(mods);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) { navigate("/login"); return; }
        setError(err.message || "Failed to load permissions.");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  /* ── Reset ────────────────────────────────────────────────── */
  const handleReset = (entry) => {
    Swal.fire({
      title: `Reset "${entry.role.roleName}" Permissions?`,
      html:  `All permissions for <strong>${entry.role.roleName}</strong> will be cleared.<br/><small class="text-muted">This cannot be undone.</small>`,
      icon:  "warning",
      showCancelButton:   true,
      confirmButtonColor: "#d33",
      cancelButtonColor:  "#6418c3",
      confirmButtonText:  "Yes, reset",
      cancelButtonText:   "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await resetRolePermissions(entry.role._id, modules);
        toast.success(
          `Permissions for "${entry.role.roleName}" have been reset.`,
          { position: "top-right", autoClose: 3000 }
        );
        load();
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) { navigate("/login"); return; }
        toast.error(err.message || "Failed to reset permissions.", {
          position: "top-right", autoClose: 4000,
        });
      }
    });
  };

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <Fragment>
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Role Menu Permissions</h4>
              <Link to="/permissions-assign" className="btn btn-primary btn-sm">
                <i className="fa fa-plus me-1" /> Assign Permissions
              </Link>
            </div>

            <div className="card-body">
              {/* Error */}
              {error && (
                <div className="alert alert-danger d-flex align-items-center justify-content-between">
                  <span><i className="fa fa-exclamation-circle me-2" />{error}</span>
                  <button className="btn btn-sm btn-outline-danger" onClick={load}>
                    <i className="fa fa-refresh me-1" /> Retry
                  </button>
                </div>
              )}

              {/* Loader */}
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                  </div>
                    <p className="mt-2 text-muted">Loading menu permissions…</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-bordered align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 44 }}>#</th>
                        <th style={{ minWidth: 150 }}>Role</th>
                        <th>Permissions Summary</th>
                        <th style={{ width: 110, textAlign: "center", whiteSpace: "nowrap" }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-5 text-muted">
                            <i className="fa fa-lock fa-2x d-block mb-2 opacity-50" />
                            No role-menu permissions found.
                          </td>
                        </tr>
                      ) : (
                        entries.map((entry, idx) => {
                          const hasAny = entry.grid.some((row) => PERMS.some((p) => row[p]));

                          return (
                            <tr key={entry.role._id}>
                              <td className="text-muted">{idx + 1}</td>

                              {/* Role */}
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="d-flex align-items-center justify-content-center rounded-circle"
                                    style={{
                                      width: 34, height: 34, flexShrink: 0,
                                      background: hasAny ? "rgba(100,24,195,0.12)" : "#f3f4f6",
                                    }}
                                  >
                                    <i
                                      className="fa fa-shield"
                                      style={{ color: hasAny ? "#6418c3" : "#9ca3af", fontSize: 14 }}
                                    />
                                  </div>
                                  <div>
                                    <span className="fw-semibold d-block">{entry.role.roleName}</span>
                                    {!hasAny && (
                                      <small className="text-muted">No permissions assigned</small>
                                    )}
                                  </div>
                                </div>
                              </td>

                              {/* Summary */}
                              <td>
                                {!hasAny ? (
                                  <span className="badge bg-secondary bg-opacity-10 text-secondary px-3 py-2">
                                    <i className="fa fa-ban me-1" /> None assigned
                                  </span>
                                ) : (
                                  <div className="d-flex flex-column gap-2">
                                    {entry.grid
                                      .filter((row) => PERMS.some((p) => row[p]))
                                      .map((row) => (
                                        <div
                                          key={row.moduleId}
                                          className="d-flex align-items-center gap-2 flex-wrap"
                                        >
                                          <span
                                            className="text-muted fw-semibold text-capitalize"
                                            style={{ minWidth: 100, fontSize: 13 }}
                                          >
                                            <span style={{ paddingLeft: `${Number(row.depth || 0) * 14}px` }}>
                                              {Number(row.depth || 0) > 0 ? "└ " : ""}
                                              {row.module}
                                            </span>
                                          </span>
                                          <div className="d-flex gap-1 flex-wrap">
                                            {PERMS.filter((p) => row[p]).map((p) => (
                                              <span
                                                key={p}
                                                className={`badge bg-${permColor[p]} ${permTextClass[p]}`}
                                                style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.2 }}
                                              >
                                                {permLabel[p]}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </td>

                              {/* Actions */}
                              <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                                <button
                                  className="btn btn-primary btn-xs me-1"
                                  title="Edit Permissions"
                                  onClick={() => navigate(`/permissions-assign?roleId=${entry.role._id}`)}
                                >
                                  <i className="fa fa-edit me-1" /> Edit
                                </button>
                                <button
                                  className="btn btn-danger btn-xs"
                                  title="Reset Permissions"
                                  onClick={() => handleReset(entry)}
                                  disabled={!hasAny}
                                >
                                  <i className="fa fa-refresh me-1" /> Reset
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ManagePermissions;